import React, { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Save,
  Loader2,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
} from "lucide-react";
import { api, resolveImage } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { TextField, Section, StringList, PageHeader, KeyValueList } from "./AdminUI";
import ImageUploader from "./ImageUploader";
import PdfUploader from "./PdfUploader";
import CaptionedImagesUploader from "./CaptionedImagesUploader";
import ContentBlocksEditor from "./ContentBlocksEditor";

const CATEGORY_OPTIONS = [
  { key: "web", label: "Web Design" },
  { key: "social", label: "Social Media" },
  { key: "vr", label: "VR Application" },
  { key: "elearning", label: "E-learning" },
  { key: "game", label: "Game Design" },
];

const COLOR_PRESETS = [
  "#FFD9C7",
  "#F7D1E0",
  "#E9E3D4",
  "#D9E6D8",
  "#FFE6A7",
  "#E1D8FF",
  "#F0DCC8",
  "#FFCFB3",
];

const empty = () => ({
  title: "New project",
  client: "",
  category: "social",
  tag: "Social Media",
  year: String(new Date().getFullYear()),
  duration: "",
  role: "",
  color: "#FFD9C7",
  cover: "",
  gallery: [],
  summary: "",
  brief: "",
  scope: [],
  deliverables: [],
  results: [],
  tools: [],
  testimonial: "",
  testimonial_by: "",
  pdfs: [],
  slug: "",
  description: "",
  tags: [],
  overview: { deliverables: "", industry: "", usage: "" },
  creative_brief: "",
  concepts: [],
  color_palette: [],
  color_palette_note: "",
  typography: {
    heading_font: { name: "", weight: "", used_for: "" },
    body_font: { name: "", weight: "", used_for: "" },
  },
  final_deliverables: [],
  mockups: [],
  print_specs: { dimensions: "", bleed: "", color_mode: "", resolution: "" },
  results_v2: [],
});

const ProjectsAdmin = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await api.listProjects());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateLocal = (id, patch) =>
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const onAdd = async () => {
    try {
      const created = await api.createProject({ ...empty(), order: items.length + 1 });
      setItems((arr) => [...arr, created]);
      setOpenId(created.id);
    } catch (e) {
      toast({ title: "Couldn't add", description: e.message });
    }
  };

  const onSave = async (item) => {
    setSavingId(item.id);
    try {
      const saved = await api.updateProject(item.id, item);
      updateLocal(item.id, saved);
      toast({ title: "Saved", description: `"${item.title}" updated.` });
    } catch (e) {
      toast({ title: "Couldn't save", description: e.message });
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this project? This can't be undone.")) return;
    try {
      await api.deleteProject(id);
      setItems((arr) => arr.filter((i) => i.id !== id));
      if (openId === id) setOpenId(null);
      toast({ title: "Deleted" });
    } catch (e) {
      toast({ title: "Couldn't delete", description: e.message });
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline-block text-[color:var(--muted)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        label="Projects"
        title="Case studies & portfolio work"
        action={
          <button onClick={onAdd} className="mk-btn mk-btn-primary">
            <Plus className="w-4 h-4" /> Add project
          </button>
        }
      />

      {items.length === 0 && (
        <div className="py-16 text-center text-[color:var(--muted)] border border-dashed border-black/15 rounded-2xl">
          No projects yet. Click “Add project” to start.
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => {
          const isOpen = openId === item.id;
          return (
            <div key={item.id} className="rounded-2xl bg-white border border-black/10 overflow-hidden">
              <button
                onClick={() => setOpenId(isOpen ? null : item.id)}
                className="w-full text-left flex items-center gap-4 p-4 md:p-5 hover:bg-black/[.02] transition-colors"
              >
                <div
                  className="w-14 h-14 md:w-16 md:h-16 rounded-xl overflow-hidden border border-black/10 grid place-items-center shrink-0"
                  style={{ backgroundColor: item.color || "#eee" }}
                >
                  {item.cover ? (
                    <img src={resolveImage(item.cover)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-[color:var(--muted)]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-semibold truncate flex items-center gap-2">
                    {item.title}
                    {item.featured && (
                      <span title="Featured on homepage" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[color:var(--coral-soft)] text-[10px] font-medium">
                        ⭐ Featured
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--muted)] truncate">
                    {item.client} {item.year ? `· ${item.year}` : ""} {item.tag ? `· ${item.tag}` : ""}
                  </div>
                </div>
                <div className="text-[color:var(--muted)]">
                  {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </div>
              </button>

              {isOpen && (
                <div className="px-4 md:px-6 pb-6 border-t border-black/10 space-y-6 pt-6">
                  <Section title="Basics">
                    <div className="grid md:grid-cols-2 gap-5">
                      <TextField label="Title" value={item.title} onChange={(v) => updateLocal(item.id, { title: v })} />
                      <TextField label="Client" value={item.client} onChange={(v) => updateLocal(item.id, { client: v })} />
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)]">Category</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {CATEGORY_OPTIONS.map((o) => (
                            <button
                              key={o.key}
                              type="button"
                              onClick={() => updateLocal(item.id, { category: o.key })}
                              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                item.category === o.key
                                  ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]"
                                  : "border-black/20 hover:border-black/50"
                              }`}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <TextField label="Tag" value={item.tag} onChange={(v) => updateLocal(item.id, { tag: v })} placeholder="Social Media / Website Design / Branding" />
                      <TextField label="Year" value={item.year} onChange={(v) => updateLocal(item.id, { year: v })} placeholder="2025" />
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                      <TextField label="Duration" value={item.duration} onChange={(v) => updateLocal(item.id, { duration: v })} placeholder="6 weeks" />
                      <TextField label="Your role" value={item.role} onChange={(v) => updateLocal(item.id, { role: v })} placeholder="Art Direction · Social Design" />
                      <TextField label="Order" type="number" value={item.order} onChange={(v) => updateLocal(item.id, { order: Number(v) || 0 })} />
                    </div>

                    <div className="mt-1 grid md:grid-cols-3 gap-5 items-end">
                      <label className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          checked={!!item.featured}
                          onChange={(e) => updateLocal(item.id, { featured: e.target.checked })}
                          className="w-4 h-4 accent-[color:var(--coral)]"
                        />
                        <span className="text-sm font-medium">⭐ Feature this project on homepage</span>
                      </label>
                      <TextField
                        label="Featured order (lower = shown first)"
                        type="number"
                        value={item.featured_order ?? ""}
                        onChange={(v) => updateLocal(item.id, { featured_order: v === "" ? null : Number(v) })}
                      />
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">Card background color</div>
                      <div className="flex flex-wrap gap-2">
                        {COLOR_PRESETS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => updateLocal(item.id, { color: c })}
                            className={`w-8 h-8 rounded-full border-2 transition-transform ${
                              item.color === c ? "border-[color:var(--ink)] scale-110" : "border-transparent"
                            }`}
                            style={{ backgroundColor: c }}
                            aria-label={`Choose ${c}`}
                          />
                        ))}
                        <input
                          type="text"
                          value={item.color || ""}
                          onChange={(e) => updateLocal(item.id, { color: e.target.value })}
                          className="ml-2 w-32 bg-transparent border border-black/15 rounded-md px-2 py-1 text-sm"
                          placeholder="#FFD9C7"
                        />
                      </div>
                    </div>
                  </Section>

                  <Section title="Cover image">
                    <ImageUploader
                      label="Cover (used in the grid + full-bleed hero)"
                      value={item.cover}
                      onChange={(v) => updateLocal(item.id, { cover: v })}
                      aspect="aspect-[16/9]"
                    />
                  </Section>

                  <Section title="Case study content" hint="Build the project page like a story — mix images, text-over-image and galleries. Drag to reorder with the arrows.">
                    <ContentBlocksEditor
                      blocks={item.content_blocks || []}
                      onChange={(v) => updateLocal(item.id, { content_blocks: v })}
                    />
                  </Section>

                  <Section title="Story sections" hint="Structured storytelling: challenge → research → design process → outcome. Every field is optional.">
                    <div className="grid md:grid-cols-2 gap-5">
                      <TextField label="Challenge heading" value={item.challenge_heading} onChange={(v) => updateLocal(item.id, { challenge_heading: v })} placeholder="What we needed to solve" />
                      <ImageUploader label="Challenge image" value={item.challenge_image} onChange={(v) => updateLocal(item.id, { challenge_image: v })} aspect="aspect-[4/3]" />
                    </div>
                    <LayoutPicker
                      label="Challenge layout"
                      value={item.challenge_layout || "image-right"}
                      onChange={(v) => updateLocal(item.id, { challenge_layout: v })}
                    />
                    <div className="grid md:grid-cols-2 gap-5 pt-4 border-t border-black/10">
                      <TextField label="Research heading" value={item.research_heading} onChange={(v) => updateLocal(item.id, { research_heading: v })} placeholder="Digging into the problem" />
                      <ImageUploader label="Research image" value={item.research_image} onChange={(v) => updateLocal(item.id, { research_image: v })} aspect="aspect-[4/3]" />
                    </div>
                    <TextField label="Research body" textarea rows={4} value={item.research_body} onChange={(v) => updateLocal(item.id, { research_body: v })} placeholder="Interviews, competitive scan, user shadowing..." />
                    <LayoutPicker
                      label="Research layout"
                      value={item.research_layout || "image-left"}
                      onChange={(v) => updateLocal(item.id, { research_layout: v })}
                    />

                    <div className="pt-4 border-t border-black/10">
                      <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">Design process steps</div>
                      <div className="text-xs text-[color:var(--muted)] mb-3">Steps automatically alternate image-left / image-right as you add them.</div>
                      <DesignProcessEditor items={item.design_process || []} onChange={(v) => updateLocal(item.id, { design_process: v })} />
                    </div>

                    <div className="grid md:grid-cols-2 gap-5 pt-4 border-t border-black/10">
                      <TextField label="Outcome heading" value={item.outcome_heading} onChange={(v) => updateLocal(item.id, { outcome_heading: v })} placeholder="What it delivered." />
                      <TextField label="Outcome body" textarea rows={4} value={item.outcome_body} onChange={(v) => updateLocal(item.id, { outcome_body: v })} placeholder="What changed for the client / users / business." />
                    </div>

                    <div className="pt-4 border-t border-black/10">
                      <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">Extras — final gallery</div>
                      <div className="text-xs text-[color:var(--muted)] mb-3">A few more images shown near the bottom of the page (process shots, alternates, misc).</div>
                      <GalleryEditor value={item.final_gallery || []} onChange={(v) => updateLocal(item.id, { final_gallery: v })} />
                    </div>
                  </Section>

                  <Section title="Project copy">
                    <TextField label="Short summary (card)" textarea rows={2} value={item.summary} onChange={(v) => updateLocal(item.id, { summary: v })} />
                    <TextField label="Description (lead paragraph on detail page)" textarea rows={3} value={item.description} onChange={(v) => updateLocal(item.id, { description: v })} />
                    <TextField label="Brief (case-study intro)" textarea rows={4} value={item.brief} onChange={(v) => updateLocal(item.id, { brief: v })} />
                    <TextField label="URL slug (auto-generated; you can override)" value={item.slug} onChange={(v) => updateLocal(item.id, { slug: v })} placeholder="aether-co-brand-identity" />
                    <StringList label="Tags (pills shown under title)" items={item.tags || []} onChange={(v) => updateLocal(item.id, { tags: v })} placeholder="e.g. Logo Design" />
                  </Section>

                  <Section title="Project Overview" hint="Three info cards on detail page">
                    <div className="grid md:grid-cols-3 gap-5">
                      <TextField label="Deliverables" value={item.overview?.deliverables} onChange={(v) => updateLocal(item.id, { overview: { ...(item.overview||{}), deliverables: v } })} placeholder="Logo Suite, Brand Guidelines, Stationery" />
                      <TextField label="Industry" value={item.overview?.industry} onChange={(v) => updateLocal(item.id, { overview: { ...(item.overview||{}), industry: v } })} placeholder="Lifestyle, Wellness" />
                      <TextField label="Usage" value={item.overview?.usage} onChange={(v) => updateLocal(item.id, { overview: { ...(item.overview||{}), usage: v } })} placeholder="Print + Digital" />
                    </div>
                  </Section>

                  <Section title="Creative Brief" hint="Highlighted box with coral accent">
                    <TextField textarea rows={5} value={item.creative_brief} onChange={(v) => updateLocal(item.id, { creative_brief: v })} placeholder="The challenge was to create a brand identity that felt..." />
                  </Section>

                  <Section title="Concept Exploration" hint="2-column grid of concept images with italic captions">
                    <CaptionedImagesUploader
                      items={item.concepts || []}
                      onChange={(v) => updateLocal(item.id, { concepts: v })}
                      captionPlaceholder="e.g. Initial concept exploring geometric forms"
                      aspect="aspect-[4/3]"
                    />
                  </Section>

                  <Section title="Color Palette" hint="5–6 swatches with name + hex">
                    <KeyValueList
                      items={item.color_palette || []}
                      onChange={(v) => updateLocal(item.id, { color_palette: v })}
                      keyName="name"
                      valueName="hex"
                      keyPlaceholder="e.g. Primary"
                      valuePlaceholder="#1A1A1A"
                    />
                    <TextField label="Palette note (optional)" textarea rows={2} value={item.color_palette_note} onChange={(v) => updateLocal(item.id, { color_palette_note: v })} placeholder="The palette draws from..." />
                  </Section>

                  <Section title="Typography" hint="Heading + body fonts">
                    <div className="grid md:grid-cols-2 gap-5">
                      <FontFields
                        label="Heading Font"
                        font={item.typography?.heading_font || {}}
                        onChange={(f) => updateLocal(item.id, { typography: { ...(item.typography||{}), heading_font: f } })}
                      />
                      <FontFields
                        label="Body Font"
                        font={item.typography?.body_font || {}}
                        onChange={(f) => updateLocal(item.id, { typography: { ...(item.typography||{}), body_font: f } })}
                      />
                    </div>
                  </Section>

                  <Section title="Final Deliverables" hint="3-column image grid with captions">
                    <CaptionedImagesUploader
                      items={item.final_deliverables || []}
                      onChange={(v) => updateLocal(item.id, { final_deliverables: v })}
                      captionPlaceholder="e.g. Business card design"
                      aspect="aspect-[3/4]"
                    />
                  </Section>

                  <Section title="Mockup Showcase" hint="Larger 2-column lifestyle / mockup images">
                    <CaptionedImagesUploader
                      items={item.mockups || []}
                      onChange={(v) => updateLocal(item.id, { mockups: v })}
                      captionPlaceholder="e.g. Stationery in use"
                      aspect="aspect-square"
                    />
                  </Section>

                  <Section title="Print Specs" hint="Optional table on detail page">
                    <div className="grid md:grid-cols-2 gap-4">
                      <TextField label="Dimensions" value={item.print_specs?.dimensions} onChange={(v) => updateLocal(item.id, { print_specs: { ...(item.print_specs||{}), dimensions: v } })} placeholder="A4 (210 × 297 mm)" />
                      <TextField label="Bleed" value={item.print_specs?.bleed} onChange={(v) => updateLocal(item.id, { print_specs: { ...(item.print_specs||{}), bleed: v } })} placeholder="3 mm" />
                      <TextField label="Color Mode" value={item.print_specs?.color_mode} onChange={(v) => updateLocal(item.id, { print_specs: { ...(item.print_specs||{}), color_mode: v } })} placeholder="CMYK" />
                      <TextField label="Resolution" value={item.print_specs?.resolution} onChange={(v) => updateLocal(item.id, { print_specs: { ...(item.print_specs||{}), resolution: v } })} placeholder="300 DPI" />
                    </div>
                  </Section>

                  <Section title="Gallery" hint="First image displays large; rest tile alongside">
                    <GalleryEditor value={item.gallery || []} onChange={(v) => updateLocal(item.id, { gallery: v })} />
                  </Section>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Section title="Scope">
                      <StringList items={item.scope || []} onChange={(v) => updateLocal(item.id, { scope: v })} placeholder="e.g. Monthly content kit" />
                    </Section>
                    <Section title="Deliverables">
                      <StringList items={item.deliverables || []} onChange={(v) => updateLocal(item.id, { deliverables: v })} placeholder="e.g. 30 feed posts" />
                    </Section>
                  </div>

                  <Section title="Tools used">
                    <StringList items={item.tools || []} onChange={(v) => updateLocal(item.id, { tools: v })} placeholder="Figma, Illustrator..." />
                  </Section>

                  <Section title="Results / metrics (simple)">
                    <KeyValueList
                      items={item.results || []}
                      onChange={(v) => updateLocal(item.id, { results: v })}
                      keyName="value"
                      valueName="label"
                      keyPlaceholder="e.g. +184%"
                      valuePlaceholder="e.g. Engagement rate"
                    />
                  </Section>

                  <Section title="Results (rich cards)" hint="Detailed cards with value + title + description (overrides simple results on detail page)">
                    <RichResultsEditor
                      items={item.results_v2 || []}
                      onChange={(v) => updateLocal(item.id, { results_v2: v })}
                    />
                  </Section>

                  <Section title="Project testimonial (optional)">
                    <TextField label="Quote" textarea rows={3} value={item.testimonial} onChange={(v) => updateLocal(item.id, { testimonial: v })} />
                    <TextField label="Quoted by" value={item.testimonial_by} onChange={(v) => updateLocal(item.id, { testimonial_by: v })} placeholder="Priya Mehta, Co-founder" />
                  </Section>

                  <Section title="Documents (PDFs)" hint="Case-study decks, brand books, etc.">
                    <PdfUploader
                      label=""
                      items={item.pdfs || []}
                      onChange={(v) => updateLocal(item.id, { pdfs: v })}
                    />
                  </Section>

                  <div className="flex justify-between pt-2">
                    <button
                      type="button"
                      onClick={() => onDelete(item.id)}
                      className="inline-flex items-center gap-2 text-sm text-[color:var(--coral)] hover:underline"
                    >
                      <Trash2 className="w-4 h-4" /> Delete project
                    </button>
                    <button
                      type="button"
                      onClick={() => onSave(item)}
                      disabled={savingId === item.id}
                      className="mk-btn mk-btn-primary disabled:opacity-60"
                    >
                      <Save className="w-4 h-4" />
                      {savingId === item.id ? "Saving…" : "Save project"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FontFields = ({ label, font = {}, onChange }) => (
  <div className="rounded-xl border border-black/10 p-4 bg-white">
    <div className="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-3">{label}</div>
    <TextField label="Font name" value={font.name} onChange={(v) => onChange({ ...font, name: v })} placeholder="Playfair Display" />
    <div className="grid sm:grid-cols-2 gap-3 mt-3">
      <TextField label="Weight" value={font.weight} onChange={(v) => onChange({ ...font, weight: v })} placeholder="Regular, Bold" />
      <TextField label="Used for" value={font.used_for} onChange={(v) => onChange({ ...font, used_for: v })} placeholder="Headlines, Display text" />
    </div>
  </div>
);

const RichResultsEditor = ({ items = [], onChange }) => {
  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { value: "", title: "", description: "" }]);
  return (
    <div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="p-4 rounded-xl border border-black/10 bg-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-[color:var(--muted)]">Result {i + 1}</span>
              <button type="button" onClick={() => remove(i)} className="text-xs text-[color:var(--coral)] hover:underline">
                Remove
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <TextField label="Value / metric" value={it.value} onChange={(v) => update(i, { value: v })} placeholder="+184% / 24 files" />
              <TextField label="Title" value={it.title} onChange={(v) => update(i, { title: v })} placeholder="Brand Recognition" />
            </div>
            <div className="mt-3">
              <TextField label="Description" textarea rows={2} value={it.description} onChange={(v) => update(i, { description: v })} placeholder="Short note about the result" />
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 inline-flex items-center gap-1.5 text-sm text-[color:var(--coral)] hover:underline">
        <Plus className="w-4 h-4" /> Add result
      </button>
    </div>
  );
};


const LayoutPicker = ({ label, value, onChange }) => {
  const OPTS = [
    { key: "image-right", label: "Image on right", hint: "Text left · Image right" },
    { key: "image-left", label: "Image on left", hint: "Image left · Text right" },
    { key: "image-below", label: "Text centered · Image below", hint: "Big landscape image under centered text" },
  ];
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">
        {label}
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        {OPTS.map((o) => (
          <button
            type="button"
            key={o.key}
            onClick={() => onChange(o.key)}
            className={`text-left p-3 rounded-xl border transition-colors ${
              value === o.key
                ? "border-[color:var(--violet)] bg-[color:var(--violet-soft)]"
                : "border-black/10 bg-white hover:border-black/25"
            }`}
          >
            <div className="text-sm font-medium">{o.label}</div>
            <div className="text-xs text-[color:var(--muted)] mt-0.5">{o.hint}</div>
          </button>
        ))}
      </div>
    </div>
  );
};


const DesignProcessEditor = ({ items = [], onChange }) => {
  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { title: "", body: "", image: "" }]);
  return (
    <div>
      <div className="space-y-3">
        {items.map((s, i) => (
          <div key={i} className="p-4 rounded-xl border border-black/10 bg-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-[color:var(--muted)]">Step {i + 1}</span>
              <button type="button" onClick={() => remove(i)} className="text-xs text-[color:var(--coral)] hover:underline">Remove</button>
            </div>
            <TextField label="Step title" value={s.title} onChange={(v) => update(i, { title: v })} placeholder="Sketching & wireframes" />
            <TextField label="Step body" textarea rows={3} value={s.body} onChange={(v) => update(i, { body: v })} placeholder="Describe what you did in this step..." />
            <ImageUploader label="Step image (optional)" value={s.image} onChange={(v) => update(i, { image: v })} aspect="aspect-[4/3]" />
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 inline-flex items-center gap-1.5 text-sm text-[color:var(--coral)] hover:underline">
        <Plus className="w-4 h-4" /> Add step
      </button>
    </div>
  );
};


const GalleryEditor = ({ value = [], onChange }) => {
  const update = (idx, v) => {
    const next = [...value];
    next[idx] = v;
    onChange(next);
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const add = () => onChange([...value, ""]);
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {value.map((url, i) => (
          <div key={i} className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-[color:var(--muted)]">
              Image {i + 1} {i === 0 && "(featured)"}
            </div>
            <ImageUploader
              label=""
              value={url}
              onChange={(v) => update(i, v)}
              aspect="aspect-[4/3]"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-xs text-[color:var(--coral)] hover:underline"
            >
              Remove image {i + 1}
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-4 inline-flex items-center gap-1.5 text-sm text-[color:var(--coral)] hover:underline"
      >
        <Plus className="w-4 h-4" /> Add image
      </button>
    </div>
  );
};

export default ProjectsAdmin;
